import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";

export default function ProcesFictifs() {
  useScrollReveal();
  return (
    <main className="ema">
      {/* HERO SECTION - Immersive */}
      <section style={{
        position: 'relative',
        minHeight: '420px',
        display: 'flex',
        alignItems: 'center',
        background: 'url(/media/photos/proces1.jpeg) center/cover no-repeat',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, rgba(11,44,95,0.88) 0%, rgba(11,44,95,0.55) 60%, rgba(11,44,95,0.3) 100%)'
        }} />
        <div className="ema-container" style={{ position: 'relative', zIndex: 2, padding: '80px 0' }}>
          <span style={{ color: '#D4AF37', fontWeight: 700, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase' }}>Pédagogie Pratique</span>
          <h1 style={{ color: '#fff', fontSize: '44px', fontWeight: 900, lineHeight: 1.1, margin: '12px 0 16px', maxWidth: '600px', letterSpacing: '-0.02em' }}>
            Procès fictifs & simulations d'audience
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '17px', lineHeight: 1.7, maxWidth: '540px', margin: '0 0 24px' }}>
            Des exercices majeurs qui confrontent les apprenants à des situations proches de la réalité pour renforcer leurs compétences.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Link className="ema-btn ema-btn-primary" to="/formations" style={{ background: '#D4AF37', color: '#071b3b' }}>
              Voir les formations
            </Link>
            <Link className="ema-btn" to="/mediatheque" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}>
              Voir la galerie
            </Link>
          </div>
        </div>
      </section>

      {/* INTRODUCTION INSTITUTIONNELLE */}
      <section className="ema-section ema-reveal">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <h2 className="ema-h2">Un exercice pédagogique majeur</h2>
              <p className="ema-lead">
                Les procès fictifs constituent un pilier essentiel de la formation des élèves magistrats
                à l'École de la Magistrature.
              </p>
            </div>
          </div>

          <div className="ema-panel" style={{ marginTop: 20 }}>
            <p className="ema-paragraph" style={{ fontSize: "17px", lineHeight: 1.8 }}>
              Les procès fictifs constituent un exercice pédagogique majeur dans la formation des élèves magistrats.
              Ils permettent de confronter les apprenants à des situations proches de la réalité juridictionnelle,
              de renforcer leur capacité d'analyse, leur maîtrise de la procédure, leur expression orale,
              leur posture professionnelle et leur sens de l'éthique judiciaire.
            </p>
            <p className="ema-paragraph" style={{ fontSize: "17px", lineHeight: 1.8, marginTop: 16 }}>
              Ces simulations d'audience offrent un cadre sécurisé où les futurs magistrats peuvent s'exercer
              aux différentes fonctions du tribunal : juge, parquet, défense et greffe. Chaque exercice est
              conçu pour reproduire les conditions réelles d'une audience, permettant ainsi une immersion
              totale dans la pratique judiciaire.
            </p>
          </div>
        </div>
      </section>

      {/* OBJECTIFS PÉDAGOGIQUES */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container">
          <h2 className="ema-h2">Objectifs pédagogiques</h2>
          <p className="ema-lead" style={{ marginBottom: 24 }}>
            Les procès fictifs visent à développer des compétences clés pour l'exercice de la fonction judiciaire.
          </p>

          <div className="ema-grid-3">
            <div className="ema-card ema-card-highlight">
              <div className="ema-card-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="ema-h3">Maîtrise de la procédure</h3>
              <p className="ema-paragraph">
                Application concrète des règles de procédure civile et pénale dans des cas fictifs.
              </p>
            </div>

            <div className="ema-card ema-card-highlight">
              <div className="ema-card-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <path d="M12 19v3"/>
                  <path d="M8 22h8"/>
                </svg>
              </div>
              <h3 className="ema-h3">Expression orale</h3>
              <p className="ema-paragraph">
                Développement de la capacité à articuler des arguments et à diriger les débats.
              </p>
            </div>

            <div className="ema-card ema-card-highlight">
              <div className="ema-card-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="ema-h3">Analyse juridique</h3>
              <p className="ema-paragraph">
                Renforcement de la capacité d'analyse critique et de qualification des faits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DÉROULEMENT DES PROCÈS FICTIFS */}
      <section className="ema-section ema-reveal">
        <div className="ema-container">
          <h2 className="ema-h2">Déroulement des procès fictifs</h2>
          <p className="ema-lead" style={{ marginBottom: 24 }}>
            Chaque procès fictif suit une structure rigoureuse reproduisant les conditions réelles d'audience.
          </p>

          <div className="ema-timeline">
            <TimelineStep
              n="1"
              title="Distribution des rôles"
              text="Chaque élève se voit attribuer un rôle : juge, procureur, avocat de la défense, greffier, témoin ou partie."
            />
            <TimelineStep
              n="2"
              title="Étude du dossier"
              text="Analyse approfondie des pièces, des faits et des enjeux juridiques du cas soumis."
            />
            <TimelineStep
              n="3"
              title="Préparation des écritures"
              text="Rédaction des conclusions, réquisitoires, plaidoiries et décisions préliminaires."
            />
            <TimelineStep
              n="4"
              title="Simulation d'audience"
              text="Déroulement de l'audience selon les règles procédurales, avec intervention de tous les acteurs."
            />
            <TimelineStep
              n="5"
              title="Débriefing et analyse"
              text="Retour d'expérience avec les formateurs sur la performance et les axes d'amélioration."
            />
          </div>
        </div>
      </section>

      {/* COMPÉTENCES DÉVELOPPÉES */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container">
          <h2 className="ema-h2">Compétences développées</h2>
          <p className="ema-lead" style={{ marginBottom: 24 }}>
            Les procès fictifs permettent de développer un ensemble de compétences professionnelles essentielles.
          </p>

          <div className="ema-grid-2">
            <div className="ema-panel">
              <h3 className="ema-h3" style={{ marginBottom: 12 }}>Compétences techniques</h3>
              <ul className="ema-list">
                <li className="ema-list-row">
                  <span>Maîtrise des règles de procédure</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Rédaction judiciaire</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Qualification juridique</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Construction de la décision</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Gestion du dossier</span>
                  <span className="ema-badge ema-badge-secondary">Important</span>
                </li>
              </ul>
            </div>

            <div className="ema-panel">
              <h3 className="ema-h3" style={{ marginBottom: 12 }}>Compétences comportementales</h3>
              <ul className="ema-list">
                <li className="ema-list-row">
                  <span>Posture professionnelle</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Expression orale</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Gestion du temps</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Sens de l'éthique</span>
                  <span className="ema-badge ema-badge-primary">Essentiel</span>
                </li>
                <li className="ema-list-row">
                  <span>Travail en équipe</span>
                  <span className="ema-badge ema-badge-secondary">Important</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT PÉDAGOGIQUE */}
      <section className="ema-section ema-reveal">
        <div className="ema-container">
          <h2 className="ema-h2">Impact pédagogique</h2>
          <p className="ema-lead" style={{ marginBottom: 24 }}>
            Les procès fictifs ont un impact significatif sur la formation et la préparation des élèves magistrats.
          </p>

          <div className="ema-stats">
            <Stat label="Simulations organisées" value="50+" />
            <Stat label="Élèves formés" value="363" />
            <Stat label="Formateurs impliqués" value="15" />
            <Stat label="Cas traités" value="120+" />
          </div>

          <div className="ema-panel" style={{ marginTop: 32 }}>
            <h3 className="ema-h3" style={{ marginBottom: 16 }}>Témoignages d'élèves</h3>
            <div className="ema-grid-2">
              <div className="ema-quote">
                <p className="ema-quote-text">
                  "Les procès fictifs m'ont permis de comprendre concrètement le rôle du juge et les défis
                  de l'audience. C'est une expérience indispensable pour ma future carrière."
                </p>
                <div className="ema-quote-author">
                  <strong>Élève magistrat</strong>
                  <span className="ema-muted">Promotion 2024</span>
                </div>
              </div>

              <div className="ema-quote">
                <p className="ema-quote-text">
                  "J'ai pu exercer tous les rôles du tribunal, ce qui m'a donné une vision complète du
                  fonctionnement de la justice et des responsabilités de chaque acteur."
                </p>
                <div className="ema-quote-author">
                  <strong>Élève magistrat</strong>
                  <span className="ema-muted">Promotion 2023</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALERIE PHOTO/VIDÉO */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 className="ema-h2">Galerie</h2>
              <p className="ema-lead" style={{ margin: 0 }}>
                Images des simulations d'audience.
              </p>
            </div>
            <Link className="ema-btn ema-btn-secondary" to="/mediatheque">
              Voir toute la galerie
            </Link>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {[
              { src: '/media/photos/proces1.jpeg', title: 'Simulation audience civile', desc: 'Promotion 2025-2027' },
              { src: '/media/photos/proces2.jpeg', title: 'Delibere collegial', desc: 'Exercice pratique' },
              { src: '/media/photos/proces3.jpeg', title: 'Plaidoiries defense', desc: 'Exercice d\'expression' },
              { src: '/media/photos/proces4.jpeg', title: 'Audience penale', desc: 'Simulation' },
              { src: '/media/photos/proces5.jpeg', title: 'Redaction jugement', desc: 'Exercice pratique' },
              { src: '/media/photos/proces6.jpeg', title: 'Remise des decisions', desc: 'Ceremonie' }
            ].map((photo, idx) => (
              <div key={idx} style={{
                borderRadius: '16px', overflow: 'hidden', background: '#fff',
                border: '1px solid #e5e7eb', transition: 'all 0.3s', cursor: 'pointer'
              }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(11,44,95,0.12)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                  <img src={photo.src} alt={photo.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.style.background = 'linear-gradient(135deg, #f3f4f6, #e5e7eb)'; }}
                  />
                </div>
                <div style={{ padding: '16px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0B2C5F', margin: '0 0 4px' }}>{photo.title}</h3>
                  <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>{photo.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA VERS FORMATIONS */}
      <section className="ema-cta">
        <div className="ema-container ema-cta-box">
          <div>
            <h2 className="ema-h2">Intéressé par la formation magistrature ?</h2>
            <p className="ema-lead">
              Découvrez nos programmes de formation initiale et continue pour devenir magistrat.
            </p>
          </div>
          <div className="ema-cta-actions">
            <Link className="ema-btn ema-btn-primary" to="/formations">
              Voir les formations
            </Link>
            <Link className="ema-btn ema-btn-secondary" to="/concours">
              Conditions d'admission
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function TimelineStep({ n, title, text }) {
  return (
    <div className="ema-step">
      <div className="ema-step-num">{n}</div>
      <div>
        <h3 className="ema-h3">{title}</h3>
        <p className="ema-paragraph">{text}</p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="ema-stat">
      <div className="ema-stat-value">{value}</div>
      <div className="ema-stat-label">{label}</div>
    </div>
  );
}
